import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { luxe } from '@/lib/luxe';

const Section = ({ title, children }) => (
  <section className="mb-8" data-testid={`policy-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <h2 className="text-lg md:text-lg font-bold mb-2" style={{ color: luxe.text }}>{title}</h2>
    <div className="text-sm leading-relaxed space-y-2" style={{ color: luxe.textMid }}>{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  const updated = 'June 2026';
  return (
    <div className="relative min-h-screen" style={{ zIndex: 1 }} data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-sm mb-8 hover:opacity-80 transition-opacity" style={{ color: luxe.muted }} data-testid="privacy-back-home">
          <ArrowLeft size={16} /> Back to Myra AI
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl" style={{ background: luxe.surface, border: `1px solid ${luxe.border}` }}>
            <ShieldCheck size={20} style={{ color: luxe.gold }} />
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: luxe.text }}>Privacy Policy</h1>
        </div>
        <p className="text-sm mb-1" style={{ color: luxe.muted }}>Myra AI — operated by Myra AI Tech Enterprises</p>
        <p className="text-sm mb-8" style={{ color: luxe.muted }}>Last updated: {updated}</p>

        <div className="rounded-2xl p-5 sm:p-8" style={{ background: luxe.bgElev, border: `1px solid ${luxe.border}` }}>
          <p className="text-sm leading-relaxed mb-8" style={{ color: luxe.textMid }}>
            This Privacy Policy explains what information Myra AI ("Myra AI", "we", "us", or "our"), a product of
            Myra AI Tech Enterprises, collects when you use our web application, how we use and protect it, and the
            choices you have. By using Myra AI, you agree to the practices described here.
          </p>

          <Section title="Information We Collect">
            <p>We collect only the information needed to provide the service, including the categories described below.</p>
          </Section>

          <Section title="Account Information">
            <p>
              When you create an account, we collect your email address and a password. Passwords are stored in a
              hashed form and are never kept in plain text. We also store basic account settings and a role that
              controls what you can access within the application.
            </p>
          </Section>

          <Section title="Social Media Account Connection Data">
            <p>
              If you choose to connect a social or messaging account, we store connection metadata such as the
              platform name, an account or channel identifier, a display name, the connection status, and the date
              connected. This data is used solely to publish or manage content that you schedule through Myra AI.
            </p>
          </Section>

          <Section title="OAuth Authentication">
            <p>
              For platforms that support it, we use the platform's official OAuth authorization flow. You sign in
              directly with the platform; Myra AI never sees your platform username or password. During this flow we
              use a short-lived, single-use, server-generated state value to protect against cross-site request
              forgery. Application credentials (such as OAuth client secrets) are stored only on our servers and are
              never sent to your browser.
            </p>
          </Section>

          <Section title="How Access and Refresh Tokens Are Protected">
            <p>
              When a platform issues an access token (and, where applicable, a refresh token), we encrypt it using
              authenticated encryption before it is stored in our database. Tokens are decrypted only on our servers,
              only at the moment they are needed to carry out an action you requested (for example, publishing a post
              you scheduled). Access and refresh tokens are never returned to your browser, never included in URLs,
              and are excluded from our application logs and error messages.
            </p>
          </Section>

          <Section title="Marketing and Lead Data">
            <p>
              The application lets you create and manage marketing content such as campaigns, posts, and leads. Any
              information you enter — including post content, schedules, and lead details you add or import — is stored
              so the application can function. You are responsible for ensuring you have the right to store and use any
              third-party personal data (such as lead contact details) that you add.
            </p>
          </Section>

          <Section title="AI Processing">
            <p>
              Some features use artificial intelligence to help generate or refine content. When you use these
              features, the text or prompts you provide may be sent to third-party AI providers that power those
              features in order to produce a result and return it to you. We do not use this content to train our own
              models. Please avoid submitting sensitive personal information in AI prompts.
            </p>
          </Section>

          <Section title="Cookies and Session Information">
            <p>
              We keep you signed in using your browser's local storage to hold your authentication token for your
              session. We do not use this mechanism for advertising or cross-site tracking.
            </p>
          </Section>

          <Section title="Data Security">
            <p>
              We take reasonable measures to protect your information: passwords are hashed, connection tokens are
              encrypted at rest, secrets are held server-side, and sensitive values are kept out of responses and
              logs. No method of transmission or storage is completely secure, and we cannot guarantee absolute
              security.
            </p>
          </Section>

          <Section title="Data Retention">
            <p>
              We retain your account and content data for as long as your account is active or as needed to provide
              the service. When you disconnect a connected platform, the stored tokens for that connection are removed.
              When you request account deletion, we delete your account data, subject to any limited retention required
              for legitimate operational or legal reasons.
            </p>
          </Section>

          <Section title="Third-Party Platforms">
            <p>
              Myra AI can connect to third-party platforms — such as LinkedIn, Instagram, Facebook, YouTube, and
              Telegram — only when you explicitly choose to connect them. The specific platforms available may vary
              over time. When you connect a platform, your use of that platform remains governed by that platform's
              own terms and privacy policy. We access only the information and permissions needed to perform the
              actions you request.
            </p>
          </Section>

          <Section title="Your Rights and Data Deletion">
            <p>
              You can review and update your account information, disconnect any connected platform at any time from
              within the application, and request access to or deletion of your personal data. To make a request,
              contact us using the details below. We will respond within a reasonable timeframe.
            </p>
          </Section>

          <Section title="Contact Information">
            <p>
              If you have questions about this Privacy Policy or wish to exercise your rights, contact us at:
            </p>
            <p style={{ color: luxe.text }}>
              Myra AI Tech Enterprises<br />
              Email: <a href="mailto:privacy@myraai.in" style={{ color: luxe.gold }} data-testid="privacy-contact-email">privacy@myraai.in</a>
            </p>
          </Section>

          <Section title="Updates to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date
              at the top of this page. Significant changes will be communicated through the application where
              appropriate. Your continued use of Myra AI after an update means you accept the revised policy.
            </p>
          </Section>
        </div>

        <p className="text-center text-xs mt-8" style={{ color: luxe.muted }}>© 2026 Myra AI Tech Enterprises. All rights reserved.</p>
      </div>
    </div>
  );
}
